import type { VifDB } from './db.js';
import { addBookmark, savePosition } from './library.js';
import { patchSettings } from './settings.js';
import type { LegacyResumeRow, SessionRow } from './types.js';

/**
 * §6.6 — first run of the new app imports the old web app's localStorage
 * (speedreader_settings / _bookmarks / _stats) exactly once. Old 10 KB-hash
 * positions land in the legacyResume store and are re-keyed when the same
 * document is re-imported (claimLegacyResume). Deliberately minimal: raw
 * legacy settings are stashed for the shell to map; nothing is deleted from
 * localStorage (the old app may still be open in another tab).
 */

/** Structural localStorage so tests and non-DOM hosts can inject one. */
export interface LegacyWebStorage {
  getItem(key: string): string | null;
}

export const LEGACY_KEYS = {
  settings: 'speedreader_settings',
  bookmarks: 'speedreader_bookmarks',
  stats: 'speedreader_stats',
} as const;

const MIGRATION_FLAG = 'migration:speedreader-localstorage';

export interface MigrationResult {
  /** False when the migration had already run (import once). */
  readonly ran: boolean;
  readonly sessionsImported: number;
  readonly legacyResumes: number;
}

function readJson(local: LegacyWebStorage, key: string): unknown {
  try {
    const raw = local.getItem(key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}

export async function migrateLegacyLocalStorage(
  db: VifDB,
  local: LegacyWebStorage,
): Promise<MigrationResult> {
  if ((await db.get('kv', MIGRATION_FLAG)) !== undefined) {
    return { ran: false, sessionsImported: 0, legacyResumes: 0 };
  }

  let sessionsImported = 0;
  let legacyResumes = 0;

  const settings = readJson(local, LEGACY_KEYS.settings);
  if (typeof settings === 'object' && settings !== null) {
    await patchSettings(db, { legacySettings: settings });
  }

  const stats = readJson(local, LEGACY_KEYS.stats) as
    | {
        totalWords?: number;
        totalTime?: number;
        totalSessions?: number;
        sessions?: { date?: string; words?: number; time?: number; avgWpm?: number }[];
      }
    | undefined;
  if (typeof stats === 'object' && stats !== null) {
    if (Array.isArray(stats.sessions)) {
      for (const legacy of stats.sessions) {
        if (typeof legacy?.words !== 'number' || typeof legacy.date !== 'string') continue;
        const row: SessionRow = {
          id: crypto.randomUUID(),
          date: legacy.date,
          words: legacy.words,
          // Prototype stored seconds.
          activeMs: Math.round((legacy.time ?? 0) * 1000),
          avgWpm: legacy.avgWpm ?? 0,
        };
        await db.put('sessions', row);
        sessionsImported++;
      }
    }
    // The prototype kept only the last 100 sessions; totals preserve the
    // full history for the stats panel.
    await patchSettings(db, {
      legacyTotals: {
        totalWords: stats.totalWords ?? 0,
        totalTime: stats.totalTime ?? 0,
        totalSessions: stats.totalSessions ?? 0,
      },
    });
  }

  const bookmarks = readJson(local, LEGACY_KEYS.bookmarks);
  if (typeof bookmarks === 'object' && bookmarks !== null) {
    for (const [legacyHash, entry] of Object.entries(bookmarks as Record<string, unknown>)) {
      if (typeof entry !== 'object' || entry === null) continue;
      const value = entry as {
        fileName?: string;
        lastPosition?: number;
        bookmarks?: unknown[];
      };
      const row: LegacyResumeRow = {
        legacyHash,
        ...(typeof value.fileName === 'string' ? { fileName: value.fileName } : {}),
        ...(typeof value.lastPosition === 'number' ? { lastPosition: value.lastPosition } : {}),
        bookmarks: Array.isArray(value.bookmarks)
          ? value.bookmarks.filter((b): b is number => typeof b === 'number')
          : [],
      };
      await db.put('legacyResume', row);
      legacyResumes++;
    }
  }

  await db.put('kv', new Date().toISOString(), MIGRATION_FLAG);
  return { ran: true, sessionsImported, legacyResumes };
}

/**
 * Re-key a legacy resume onto a real library document. Called at import
 * time with every legacy identity the new document could have had
 * (legacyFileHash of the bytes; legacyTextHash for pastes). First match
 * wins, is applied (position + bookmarks) and the legacy row is deleted.
 */
export async function claimLegacyResume(
  db: VifDB,
  docId: string,
  candidateHashes: readonly string[],
  now?: number,
): Promise<LegacyResumeRow | undefined> {
  for (const hash of candidateHashes) {
    const row = await db.get('legacyResume', hash);
    if (row === undefined) continue;
    if (typeof row.lastPosition === 'number' && row.lastPosition > 0) {
      await savePosition(db, docId, row.lastPosition, now);
    }
    for (const wordIdx of row.bookmarks) {
      await addBookmark(db, docId, wordIdx, '', now);
    }
    await db.delete('legacyResume', hash);
    return row;
  }
  return undefined;
}
